<template>
  <div class="image-carousel-item" :style="style">
    <AppImageFigure
        :data="data"
        :renditions="currentRenditions() as TImageRendition[]"
        caption-classes="image-carousel-caption"
    />
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, reactive} from 'vue'
import {TImageBlock} from '~/types/block-types'
import {useBreakpointsStore} from '~/store/breakpoints'
import {breakpoints} from '~/constants/breakpoints'
import type {TImageRendition} from '~/types/other-types'

const breakpointsStore = useBreakpointsStore()

const props = defineProps<{
  data: TImageBlock
}>()

// 16:9
const renditionsLandscape = [
  {width: props.data.contain ? 'auto' : 325, height: 183, breakpoint: 'xs', original: 325},
  {width: props.data.contain ? 'auto' : 563, height: 317, breakpoint: 'md', original: 563},
  {width: props.data.contain ? 'auto' : 756, height: 425, breakpoint: 'lg', original: 756},
  {width: props.data.contain ? 'auto' : 892, height: 502, breakpoint: 'xl', original: 892},
  {width: props.data.contain ? 'auto' : 1088, height: 612, breakpoint: 'xxl', original: 1088},
]

// 4:5
const renditionsPortrait = [
  {width: props.data.contain ? 'auto' : 147, height: 183, breakpoint: 'xs', original: 147},
  {width: props.data.contain ? 'auto' : 254, height: 317, breakpoint: 'md', original: 254},
  {width: props.data.contain ? 'auto' : 340, height: 425, breakpoint: 'lg', original: 340},
  {width: props.data.contain ? 'auto' : 402, height: 502, breakpoint: 'xl', original: 402},
  {width: props.data.contain ? 'auto' : 490, height: 612, breakpoint: 'xxl', original: 490}
]

const style = computed(() => {
  const mobileWidth = props.data.orientation === 'landscape' ? '85vw' : '38.5vw'
  const width = (state.breakpoint === 0) ? mobileWidth : `${(state.size.original)}px`
  return {
    width: width,
  }
})

const currentRenditions = () => {
  return (props.data.orientation === 'landscape') ? renditionsLandscape : renditionsPortrait
}

const getSize = () => {
  const renditions = currentRenditions()
  for (let i = breakpointsStore.getCurrentBreakpointIndex; i >= 0; i--) {
    const foundSource = renditions.find((source) => {
      return source.breakpoint === breakpoints[i].name
    })
    if (foundSource) {
      return foundSource
    }
  }

  // fallback
  return renditions[0]
}

const state = reactive({
  size: {...getSize()},
  breakpoint: 0
})

// watch breakpoint change
onMounted(() => {
  state.size = {...getSize()}
  breakpointsStore.$subscribe(() => {
    state.size = {...getSize()}
  })
})

// update the image size based on the current breakpoint
onMounted(() => {
  breakpointsStore.$subscribe(() => {
    state.breakpoint = breakpointsStore.getCurrentBreakpointIndex
  })
  state.breakpoint = breakpointsStore.getCurrentBreakpointIndex
})

</script>

<style lang="postcss">
.swiper-slide .image-figure__caption {
  opacity: 0;
  transition-property: color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter;
  transition-duration: 300ms;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);

  &--show {
    opacity: 1;
    transition-property: color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter;
    transition-delay: 150ms;
    transition-duration: 300ms;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  }
}
</style>
